/**
 * Builds the full self-contained HTML document for an anonymised candidate PDF.
 *
 * Uses the domain PdfCardDef renderers from @brainpool/profile-schema so that
 * every domain card is rendered identically whether previewed in the browser,
 * printed via the browser print dialog, or rasterised with Puppeteer.
 *
 * The document is designed for a single-page, tall-aspect-ratio PDF:
 *   @page { size: 860px auto; margin: 0; }
 * Puppeteer should set { width: '860px', height: <scrollHeight>px }.
 */

import {
  LAYOUT,
  PositionsPdfCard,
  EducationPdfCard,
  SkillsPdfCard,
  LanguagesPdfCard,
  HonorsPdfCard,
  CertificationsPdfCard,
  PublicationsPdfCard,
  PatentsPdfCard,
  ArticlesPdfCard,
  CoursesPdfCard,
  OrganizationsPdfCard,
  ProjectsPdfCard,
  type PdfCardDef,
} from '@brainpool/profile-schema';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AnonymizedExportDomain {
  domain: string;
  records: Array<Record<string, unknown>>;
}

export interface AnonymizedExportProfile {
  candidateId: number;
  generatedAt: string;
  profile: {
    professionalHeadline: string | null;
    yearsOfExperience: number | null;
    industry: string | null;
    mostRecentJobTitle: string | null;
    highestEducationLevel: string | null;
    candidateSummary: string | null;
  };
  domains: AnonymizedExportDomain[];
  redaction?: {
    strategy?: string;
    model?: string;
  };
}

// ─── Card registry ────────────────────────────────────────────────────────────

const CARD_MAP: Record<string, PdfCardDef> = {
  POSITIONS: PositionsPdfCard,
  EDUCATION: EducationPdfCard,
  SKILLS: SkillsPdfCard,
  LANGUAGES: LanguagesPdfCard,
  HONORS: HonorsPdfCard,
  CERTIFICATIONS: CertificationsPdfCard,
  PUBLICATIONS: PublicationsPdfCard,
  PATENTS: PatentsPdfCard,
  ARTICLES: ArticlesPdfCard,
  COURSES: CoursesPdfCard,
  ORGANIZATIONS: OrganizationsPdfCard,
  PROJECTS: ProjectsPdfCard,
};

// ─── SVG logo mark (coloured, brand palette) ─────────────────────────────────

const MONO_SVG = `<svg width="24" height="24" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="10" cy="10" r="9"   fill="#a2dacd"/>
  <circle cx="10" cy="10" r="5.5" fill="#77cab7"/>
  <circle cx="10" cy="10" r="3.4" fill="#76a5b2"/>
  <circle cx="10" cy="10" r="2.2" fill="#5e5ba8"/>
</svg>`;

// ─── Fibonacci decoration circles (top-right hero overlay) ───────────────────
// Radii follow the same proportions as the logo mark (240:148:91:58), scaled
// so the outermost circle radius ≈ 175px. Centre is pinned to the top-right
// corner; .hero { overflow: hidden } clips the parts that bleed outside.

const HERO_CIRCLES_SVG = `<svg class="hero-circles" viewBox="0 0 400 320" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMin slice">
  <circle cx="400" cy="0" r="175" fill="#a2dacd" fill-opacity="0.11"/>
  <circle cx="400" cy="0" r="108" fill="#77cab7" fill-opacity="0.14"/>
  <circle cx="400" cy="0" r="67"  fill="#76a5b2" fill-opacity="0.19"/>
  <circle cx="400" cy="0" r="42"  fill="#5e5ba8" fill-opacity="0.25"/>
</svg>`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function cardHeader(label: string): string {
  return `<div class="card-header"><span>${label}</span><div class="card-accent"></div></div>`;
}

function computeYearsFromPositions(positions: Array<Record<string, unknown>>): number | null {
  let earliest: number | null = null;
  for (const pos of positions) {
    const raw = pos['startDate'] as string | null | undefined;
    if (!raw) continue;
    const yr = new Date(raw).getFullYear();
    if (!isNaN(yr) && (earliest === null || yr < earliest)) earliest = yr;
  }
  return earliest !== null ? new Date().getFullYear() - earliest : null;
}

function renderHero(
  payload: AnonymizedExportProfile,
  domainMap: Map<string, Array<Record<string, unknown>>>
): string {
  const { profile } = payload;
  const byDomain = (key: string) => domainMap.get(key) ?? [];
  const yearsOfExperience =
    profile.yearsOfExperience ?? computeYearsFromPositions(byDomain('POSITIONS'));
  const headline = [profile.mostRecentJobTitle, profile.highestEducationLevel]
    .filter(Boolean)
    .join(' &nbsp;&middot;&nbsp; ');

  return `
    <div class="hero">
      ${HERO_CIRCLES_SVG}
      <div class="hero-brand">
        ${MONO_SVG}
        BRAINPOOL.AI
        <span class="hero-id-badge">Candidate #${payload.candidateId}</span>
      </div>
      <div class="hero-name">Anonymised Candidate</div>
      ${headline ? `<div class="hero-headline">${headline}</div>` : ''}
      ${profile.industry ? `<div class="hero-industry">${profile.industry}</div>` : ''}
      <div class="hero-stats">
        ${yearsOfExperience != null ? `<div><div class="hero-stat-num">${yearsOfExperience}+</div><div class="hero-stat-label">Years Exp.</div></div>` : ''}
        ${byDomain('POSITIONS').length ? `<div><div class="hero-stat-num">${byDomain('POSITIONS').length}</div><div class="hero-stat-label">Positions</div></div>` : ''}
        ${byDomain('EDUCATION').length ? `<div><div class="hero-stat-num">${byDomain('EDUCATION').length}</div><div class="hero-stat-label">Degrees</div></div>` : ''}
        ${byDomain('LANGUAGES').length ? `<div><div class="hero-stat-num">${byDomain('LANGUAGES').length}</div><div class="hero-stat-label">Languages</div></div>` : ''}
        ${byDomain('SKILLS').length ? `<div><div class="hero-stat-num">${byDomain('SKILLS').length}</div><div class="hero-stat-label">Skills</div></div>` : ''}
      </div>
    </div>`;
}

function renderSummary(payload: AnonymizedExportProfile): string {
  const text = payload.profile.candidateSummary;
  if (!text) return '';
  return `<div class="card">${cardHeader('Candidate Summary')}<p class="summary-text">${text}</p></div>`;
}

function renderCard(
  domainName: string,
  label: string,
  domainMap: Map<string, Array<Record<string, unknown>>>
): string {
  const records = domainMap.get(domainName) ?? [];
  if (!records.length) return '';
  const card = CARD_MAP[domainName];
  if (!card) return '';
  const body = card.render(records);
  if (!body.trim()) return '';
  return `<div class="card">${cardHeader(label)}${body}</div>`;
}

// ─── Main builder ─────────────────────────────────────────────────────────────

/**
 * Builds a complete, self-contained HTML document for the given candidate
 * profile. Suitable for browser preview, browser print-to-PDF, and Puppeteer.
 *
 * @param payload      The anonymised export payload from the API.
 * @param autoPrint    When true, adds `window.onload = () => window.print()`
 *                     so the document auto-triggers the print dialog when
 *                     opened in a browser tab.
 */
export function buildCandidatePdfHtml(
  payload: AnonymizedExportProfile,
  { autoPrint = false }: { autoPrint?: boolean } = {}
): string {
  const domainMap = new Map(payload.domains.map(d => [d.domain, d.records]));

  const domainStyles = LAYOUT.map(l => CARD_MAP[l.domain]?.styles ?? '').join('\n');

  const mainCards = LAYOUT.filter(l => l.column === 'main')
    .map(l => renderCard(l.domain, l.label, domainMap))
    .join('');
  const sideCards = LAYOUT.filter(l => l.column === 'side')
    .map(l => renderCard(l.domain, l.label, domainMap))
    .join('');
  const overflowCards = LAYOUT.filter(l => l.column === 'overflow')
    .map(l => renderCard(l.domain, l.label, domainMap))
    .filter(Boolean)
    .join('');

  const d = new Date(payload.generatedAt);
  const dateStr = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Brainpool — Candidate #${payload.candidateId} Profile</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,300;0,400;0,700;0,900;1,300;1,400&display=swap" rel="stylesheet" />
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    :root {
      --ink: #1b1b1b; --body: #484c52; --muted: #9398a0; --line: #e2e2e2;
      --primary: #5e5ba8; --primary-soft: #eeedfe;
      --accent: #77c9b8; --accent-alt: #5abaa7; --accent-soft: #e8f7f4;
      --blue-gray: #76a5b2; --white: #ffffff;
    }

    /* ── Screen wrapper (stripped away in print) ── */
    body { font-family: 'Lato', sans-serif; background: #e8e6e3; color: var(--ink); -webkit-font-smoothing: antialiased; }
    .page-outer { padding: 2.5rem 2rem; }
    .page { max-width: 860px; margin: 0 auto; background: #f6f6f5; border-radius: 6px; overflow: hidden; box-shadow: 0 6px 48px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.08); }

    /* ── Inner layout ── */
    .page-inner { padding: 1.5rem; display: flex; flex-direction: column; gap: 1.25rem; }

    /* ── Hero ── */
    .hero { background: linear-gradient(135deg, #5e5ba8 0%, #4a4890 50%, #76a5b2 100%); border-radius: 12px; padding: 2.5rem 2.5rem 2rem; color: var(--white); position: relative; overflow: hidden; }
    .hero-circles { position: absolute; top: 0; right: 0; width: 400px; height: 100%; pointer-events: none; }
    .hero-brand { display: flex; align-items: center; gap: 9px; font-size: 11.5px; letter-spacing: 0.28em; text-transform: uppercase; opacity: 0.85; margin-bottom: 1.75rem; position: relative; }
    .hero-id-badge { background: rgba(255,255,255,0.1); padding: 3px 11px; border-radius: 20px; font-size: 10.5px; letter-spacing: 0.08em; }
    .hero-name { font-size: 34px; font-weight: 900; letter-spacing: -0.5px; line-height: 1.1; position: relative; }
    .hero-headline { font-size: 13px; opacity: 0.6; margin-top: 7px; font-weight: 300; position: relative; }
    .hero-industry { font-size: 11px; opacity: 0.45; margin-top: 4px; font-weight: 400; letter-spacing: 0.04em; text-transform: uppercase; position: relative; }
    .hero-stats { display: flex; gap: 2.25rem; margin-top: 2rem; padding-top: 1.5rem; border-top: 1px solid rgba(255,255,255,0.12); position: relative; flex-wrap: wrap; }
    .hero-stat-num { font-size: 22px; font-weight: 900; }
    .hero-stat-label { font-size: 9px; letter-spacing: 0.15em; text-transform: uppercase; opacity: 0.5; margin-top: 2px; }

    /* ── Cards ── */
    .card { background: var(--white); border-radius: 10px; padding: 1.25rem 1.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.03); }
    .card-header { display: flex; align-items: center; justify-content: space-between; font-size: 9px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: var(--primary); margin-bottom: 1.1rem; }
    .card-accent { width: 22px; height: 3px; background: var(--accent); border-radius: 2px; }

    /* ── Layout columns ── */
    .two-col { display: grid; grid-template-columns: 1fr 0.62fr; gap: 1.25rem; align-items: start; }
    .col { display: flex; flex-direction: column; gap: 1.25rem; }
    .overflow-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 1.25rem; }

    /* ── Shared tokens ── */
    .summary-text { font-size: 13px; line-height: 1.9; color: var(--body); font-weight: 300; }
    .bp-current { font-size: 8.5px; padding: 2px 7px; background: var(--accent-soft); color: var(--accent-alt); border-radius: 20px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
    .bp-present { color: var(--accent-alt); font-weight: 700; }
    .bp-sep { opacity: 0.4; }
    .bp-grade { font-size: 10px; padding: 1px 7px; background: var(--primary-soft); color: var(--primary); border-radius: 20px; font-weight: 700; display: inline-block; margin-top: 4px; }
    .bp-status { font-size: 8.5px; padding: 2px 7px; background: var(--primary-soft); color: var(--primary); border-radius: 20px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
    .bp-note { font-size: 11.5px; color: var(--body); font-weight: 300; line-height: 1.65; margin-top: 6px; }

    /* ── Footer ── */
    .page-footer { padding: 0.7rem 1.5rem; background: #efeeec; border-top: 1px solid var(--line); display: flex; justify-content: space-between; align-items: center; font-size: 9px; color: var(--muted); letter-spacing: 0.06em; }

    /* ── Print / PDF ── */
    @media print {
      body { background: white; }
      .page-outer { padding: 0; }
      .page { box-shadow: none; border-radius: 0; max-width: 100%; }
    }
    /* Single-page tall layout: width is fixed, height follows content */
    @page { margin: 0; }

    /* ── Domain card styles (injected from PdfCardDef.styles) ── */
    ${domainStyles}
  </style>
  ${autoPrint ? '<script>window.onload = function() { window.print(); }</script>' : ''}
</head>
<body>
<div class="page-outer">
  <div class="page">
    <div class="page-inner">
      ${renderHero(payload, domainMap)}
      ${renderSummary(payload)}
      <div class="two-col">
        <div class="col">${mainCards}</div>
        <div class="col">${sideCards}</div>
      </div>
      ${overflowCards ? `<div class="overflow-grid">${overflowCards}</div>` : ''}
    </div>
    <div class="page-footer">
      <span>BRAINPOOL CONFIDENTIAL — ANONYMISED CANDIDATE DATA</span>
      <span>Generated ${dateStr}</span>
    </div>
  </div>
</div>
</body>
</html>`;
}

// ─── Browser print helper ─────────────────────────────────────────────────────

/**
 * Opens the candidate profile HTML in a new browser tab and triggers the print
 * dialog automatically so the user can save it as a PDF.
 *
 * Use this for the interactive "Export PDF" button in the admin app.
 * For server-side / automated PDF generation use `generate-pdf.ts` (Puppeteer).
 */
export function openCandidatePdfTab(payload: AnonymizedExportProfile): void {
  const html = buildCandidatePdfHtml(payload, { autoPrint: true });
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  // Revoke after a generous delay to ensure the new tab has loaded.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
